import { Button, Card } from '@/lib';
import { PerformanceAudienceComparisonRow } from '@/services/endpoints/performanceAudience';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { GoogleAdsIcon, MetaIcon } from '../PlatformBrandIcons';

type PerformanceAudienceComparisonTableProps = {
  rows: PerformanceAudienceComparisonRow[];
  onExportCsv?: () => void;
};

export const PerformanceAudienceComparisonTable: FC<PerformanceAudienceComparisonTableProps> = ({ rows, onExportCsv }) => {
  const { t } = useTranslation('performanceAudience');

  const renderCell = (cell: PerformanceAudienceComparisonRow['google']) => {
    if (cell.unavailable) {
      return <span className='text-sm text-tertiary-500'>{t('comparison.notAvailable')}</span>;
    }

    return <span className='text-sm font-medium text-primary-900'>{cell.value}</span>;
  };

  return (
    <Card className='flex h-full flex-col gap-6 border-primary-border bg-white p-6 drop-shadow-none'>
      <header className='flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between'>
        <div className='grid gap-0.5'>
          <h2 className='text-base font-semibold text-primary-900'>{t('comparison.title')}</h2>
          <p className='text-sm text-tertiary-600'>{t('comparison.subtitle')}</p>
        </div>
        <Button intent='secondary' size='sm' onClick={onExportCsv}>
          {t('comparison.exportCsv')}
        </Button>
      </header>
      <div className='overflow-x-auto'>
        <table className='w-full min-w-[28rem] border-collapse text-left'>
          <thead>
            <tr className='border-b border-primary-border'>
              <th className='pb-3 pr-4 text-xs font-semibold uppercase tracking-wide text-tertiary-600'>{t('comparison.metric')}</th>
              <th className='pb-3 px-4 text-xs font-semibold uppercase tracking-wide text-tertiary-600'>
                <span className='inline-flex items-center gap-2'>
                  <GoogleAdsIcon />
                  {t('platforms.googleAds')}
                </span>
              </th>
              <th className='pb-3 pl-4 text-xs font-semibold uppercase tracking-wide text-tertiary-600'>
                <span className='inline-flex items-center gap-2'>
                  <MetaIcon />
                  {t('platforms.meta')}
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className='border-b border-primary-border last:border-b-0'>
                <td className='py-3 pr-4 text-xs font-semibold uppercase tracking-wide text-tertiary-600'>
                  {t(`comparison.rows.${row.id}` as 'comparison.rows.impressions')}
                </td>
                <td className={twMerge('py-3 px-4', row.google.unavailable && 'text-tertiary-500')}>{renderCell(row.google)}</td>
                <td className={twMerge('py-3 pl-4', row.meta.unavailable && 'text-tertiary-500')}>{renderCell(row.meta)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
};
